import type { ReactNode } from 'react'
import { ChatAgentReportCard } from './chat-agent-report-card.molecule'
import { ChatContentBriefItem } from './chat-content-brief-item.molecule'
import { ChatDelegationCard } from './chat-delegation-card.molecule'
import { isOwnItem } from './chat-item-content.helper'
import type { RenderItemParams } from './chat-item-renderer.interface'
import type {
  ChatAgentReport,
  ChatContentBrief,
  ChatDelegation,
  ChatMessage,
  ChatToolCall,
  ChatUserInteraction,
} from './chat-message.interface'
import { ChatMessageItem } from './chat-message-item.molecule'
import { ChatThoughtIndicator } from './chat-thought-indicator.atom'
import { ChatToolCallItem } from './chat-tool-call-item.molecule'
import { ChatUserInteractionItem } from './chat-user-interaction-item.molecule'

export const renderMessageOrNote = (params: RenderItemParams, isPinned: boolean): ReactNode => {
  const {
    item,
    currentUserId,
    highlightedMessageId,
    onMessageClick,
    onPinItem,
    onAttachmentDownload,
  } = params
  if (item.type !== 'message' && item.type !== 'note') return null

  const isOwn = isOwnItem(item, currentUserId)
  return (
    <ChatMessageItem
      message={item}
      isOwn={isOwn}
      agentName={item.type === 'message' ? item.participant?.name : undefined}
      isPinned={isPinned}
      isHighlighted={highlightedMessageId === item.id}
      onPin={onPinItem}
      onClick={onMessageClick}
      onAttachmentDownload={onAttachmentDownload}
    />
  )
}

export const renderToolCall = (params: RenderItemParams, isPinned: boolean): ReactNode => {
  const { item, highlightedMessageId, onToolCallClick, onPinItem } = params
  if (item.type !== 'tool-call') return null
  const toolCall = item as ChatToolCall
  return (
    <ChatToolCallItem
      toolCall={toolCall}
      agentName={toolCall.participant?.name}
      isPinned={isPinned}
      isHighlighted={highlightedMessageId === item.id}
      onPin={onPinItem}
      onClick={onToolCallClick}
    />
  )
}

export const renderContentBrief = (params: RenderItemParams, isPinned: boolean): ReactNode => {
  const { item, highlightedMessageId, onContentBriefClick, onPinItem } = params
  if (item.type !== 'content-brief') return null
  return (
    <ChatContentBriefItem
      contentBrief={item as ChatContentBrief}
      isPinned={isPinned}
      isHighlighted={highlightedMessageId === item.id}
      onPin={onPinItem}
      onClick={onContentBriefClick}
    />
  )
}

export const renderUserInteraction = (params: RenderItemParams, isPinned: boolean): ReactNode => {
  const { item, highlightedMessageId, onUserInteractionClick, onPinItem } = params
  if (item.type !== 'user-interaction') return null
  return (
    <ChatUserInteractionItem
      interaction={item as ChatUserInteraction}
      isPinned={isPinned}
      isHighlighted={highlightedMessageId === item.id}
      onPin={onPinItem}
      onClick={onUserInteractionClick}
    />
  )
}

export const renderThinking = (params: RenderItemParams): ReactNode => {
  const { item } = params
  if (item.type !== 'thinking') return null
  return <ChatThoughtIndicator className="chat-thinking" />
}

export const renderAgentReport = (params: RenderItemParams, isPinned: boolean): ReactNode => {
  const { item, highlightedMessageId, onReportClick, onPinItem } = params
  if (item.type !== 'agent-report') return null
  const report = item as ChatAgentReport
  const displayName = report.agentName ?? report.agentId ?? 'Agent'
  const reportMessage = buildReportMessage(report, displayName)
  return (
    <ChatAgentReportCard
      reportMessage={reportMessage}
      agentName={displayName}
      isLoading={report.isLoading}
      isPinned={isPinned}
      isHighlighted={highlightedMessageId === item.id}
      onPin={onPinItem}
      onClick={onReportClick}
    />
  )
}

const buildReportMessage = (report: ChatAgentReport, displayName: string): ChatMessage => ({
  id: report.id,
  type: 'message',
  sender: 'assistant',
  content: report.reportContent || '',
  timestamp: report.timestamp,
  participant: { id: report.agentId ?? 'agent', name: displayName },
  isStreaming: report.isLoading,
  reportId: report.reportId,
  reportVersion: report.reportVersion,
  isMasterArtifact: report.isMasterArtifact,
  reportFormat: report.reportFormat,
})

export const renderDelegation = (params: RenderItemParams): ReactNode => {
  const { item } = params
  if (item.type !== 'delegation') return null
  return <ChatDelegationCard delegation={item as ChatDelegation} />
}
